import axios from "axios";
import dayjs from 'dayjs';
import { Base64 } from "js-base64";
import toast from "react-hot-toast";
import debounce from "lodash/debounce";
import ReportTable from "./ReportTable";
import { Box, Button } from "@mui/material";
import { origin } from "../../Config/Api-constants";
import { useEffect, useState } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import "../../../Assets/Styles/Containers/_reportStatusPage.scss";

const BASE_URL = await origin();
const ReportStatusPage = () => {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [status, setStatus] = useState([]);
    const [selectedDate, setSelectedDate] = useState('This week');
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [hasInitialized, setHasInitialized] = useState(false);
    const [inputText, setInputText] = useState("");     // what the user sees
    const [searchQuery, setSearchQuery] = useState(""); // what the API uses
    const [favicon, setFavicon] = useState();
    const [sortBy, setSortBy] = useState('updated_at');
    const [sortDirection, setSortDirection] = useState('desc');

    useEffect(() => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const encodedKey = urlParams.get("key");

            if (encodedKey) {
                const decoded = Base64.decode(encodedKey);
                const decodedParams = new URLSearchParams(decoded);

                decodedParams.forEach((value, key) => {
                    sessionStorage.setItem(key, value);
                });

                window.history.replaceState({}, document.title, window.location.pathname);
            }

            const apiKey = sessionStorage.getItem("company_key");
            const secretKey = sessionStorage.getItem("secret_key");

            if (!apiKey || !secretKey) {
                throw new Error("Missing required credentials.");
            }



            fetchReports(); // Initial fetch
            setHasInitialized(true);
        } catch (err) {
            toast.error(err.message || "Something went wrong");
        }
    }, []);


    useEffect(() => {
        if (favicon) {
            const timestamp = Date.now();
            const randomValue = Math.random().toString(36).substring(7);
            const faviconUrl = `${favicon}?t=${timestamp}_${randomValue}`;
            const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
            link.rel = 'icon';
            link.href = faviconUrl;
            const headElement = document.querySelector('head');
            const existingFavicon = document.querySelector('link[rel="icon"]');
            if (existingFavicon) {
                headElement.removeChild(existingFavicon);
            }
            headElement.appendChild(link);
            document.title = 'Web - Report Status';
        }
    }, [favicon]);

    const fetchReports = async (pageNum = 1, searchQuery = '', status = 'All', fromDate = null, toDate = null, sortField = 'updated_at', sortDirection = 'desc') => {
        const apiKey = sessionStorage.getItem("company_key");
        const secretKey = sessionStorage.getItem("secret_key");

        if (!apiKey || !secretKey) {
            console.error("API credentials missing in sessionStorage");
            return;
        }

        setLoading(true);
        try {
            const rows = rowsPerPage;
            let url = `${BASE_URL}company-reports?page=${pageNum}&rows_per_page=${rows}`;

            if (searchQuery) {
                url += `&search=${encodeURIComponent(searchQuery)}`;
            }
            if (Array.isArray(status) && status.length > 0) {
                status.forEach(stat => {
                    url += `&status[]=${encodeURIComponent(stat.toUpperCase())}`;
                });
            }
            // might require later
            // if (fromDate && toDate) {
            //     url += `&from_date=${fromDate}&to_date=${toDate}`;
            // }

            url += `&sort_field=${sortField}&sort_direction=${sortDirection}`;

            const response = await axios.get(url, {
                headers: {
                    "api-key": apiKey,
                    "Authorization": `Bearer ${secretKey}`,
                    "platform": 'web',
                    "api-platform": 'WRW',
                    "user_id": sessionStorage.getItem('UID'),
                    "product": sessionStorage.getItem('asoCom'),
                    "mode": 'online',
                }
            });

            const apiData = response.data?.data;
            if (apiData?.status === "success") {
                setData(apiData.data);
                setFavicon(apiData?.favicon);
                setTotalCount(apiData.pagination.total_count);
                setHasInitialized(true);
            } else {
                console.warn("Unexpected API response:", apiData);
                // toast.error(apiData)
            }
        } catch (err) {
            console.error("Fetch error:", err);
            // toast.error(err)
        } finally {
            setLoading(false);
        }
    };


    const changeStatus = async (order) => {
        console.log(order, "order")
        const apiKey = sessionStorage.getItem("company_key");
        const secretKey = sessionStorage.getItem("secret_key");
        const userID = sessionStorage.getItem("user_id");

        if (!apiKey || !secretKey) {
            console.error("API credentials missing in sessionStorage");
            return;
        }

        setLoading(true);

        try {
            const url = `${BASE_URL}change-order-status`; // <-- new endpoint

            const payload = {
                "guid": order?.orderGuid,
                "user_id": userID,
                "role_id": "5",
                "company_id": apiKey,
                "status_date": order?.orderStartDate,
                "status": "In-Progress"// use PascalCase like 'In-Progress' or whatever API expects
            };

            const response = await axios.post(url, payload, {
                headers: {
                    "api-key": apiKey,
                    "Authorization": `Bearer ${secretKey}`,
                    "Content-Type": "application/json"
                }
            });

            const apiData = response.data;

            if (apiData?.status === "success") {
                // toast.success("Status updated successfully!");
                // refetch or update local state here if needed
            } else {
                toast.error(apiData?.message || "Failed to update status");
            }
        } catch (err) {
            console.error("API error:", err);
            // toast.error("Something went wrong while updating status");
        } finally {
            setLoading(false);
        }
    };


    const clearAllFilters = () => {
        setSearchQuery('');
        setInputText('');
        setSelectedDate('This week');
        setStatus([]);  // or default value
        setFromDate(dayjs().startOf('week').format('YYYY-MM-DD'));
        setToDate(dayjs().endOf('week').format('YYYY-MM-DD'));
        setPage(0);
    };


    // 🧠 Debounced API call based on search text
    useEffect(() => {
        const handler = debounce(() => {
            setSearchQuery(inputText); // this triggers the API
            setPage(0);                // reset to first page
        }, 1000);

        handler();
        return () => handler.cancel();
    }, [inputText]);

    // Refetch on pagination/rows change
    useEffect(() => {
        if (hasInitialized) {
            fetchReports(page + 1, searchQuery, status, fromDate, toDate, sortBy, sortDirection); // API uses debounced query
        }
    }, [page, rowsPerPage, searchQuery, status, fromDate, toDate, sortBy, sortDirection]);

    return (
        <Box className="report_status_page_container" sx={{ position: 'relative' }}>
            <div className="report_status_table_container">
                <div className="report_status_page_header">
                    <span className="report_status_page_text">My reports</span>
                    <span>
                        <Button
                            onClick={() => {
                                window.open(`https://4isn.com/${sessionStorage.getItem("company_key")}/orders/complete-v2`, "_blank");
                            }}
                            className="report_status_page_header_button"
                            startIcon={<OpenInNewIcon />}
                        >
                            Open my inspections
                        </Button>
                    </span>
                </div>
                <div className="report_status_page_table_content">
                    <ReportTable
                        data={data}
                        page={page}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        search={inputText}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        sortDirection={sortDirection}
                        setSortDirection={setSortDirection}
                        setInputText={setInputText}
                        status={status}
                        setStatus={setStatus}
                        changeStatus={changeStatus}
                        loading={loading}
                        setPage={setPage}
                        clearAllFilters={clearAllFilters}
                        setFromDate={setFromDate}
                        setToDate={setToDate}
                        rowsPerPage={rowsPerPage}
                        totalCount={totalCount}
                        onPageChange={setPage}
                        onRowsPerPageChange={(newLimit) => {
                            setRowsPerPage(newLimit);
                            setPage(0);
                        }}
                    />
                </div>
            </div>
        </Box>
    );
};

export default ReportStatusPage;